/**
 * presets.ts — ship-default input sets and Read Me benchmark data for the ROI engine (T-020).
 *
 * Values are transcribed verbatim from Noreen's `OptiYield_ROI_Calculators.xlsx` (source_materials/,
 * internal): `DCFC_PRESET` from the "DCFC ROI" tab, `L2_PRESET` from the "L2 ROI" tab, and
 * `BENCHMARK_RANGES` / `INCENTIVE_NOTES` from the "Read Me" tab. The presets are the sheet's
 * pre-filled "defensible defaults" a user is meant to overwrite for their own site — directional
 * 2025–2026 US benchmarks, NOT a quote or investment advice.
 *
 * The DCFC default never recovers its CapEx within 10 years, and the L2 default is intentionally
 * loss-making at ship values — both are honest starting points, not marketing numbers.
 */
import type { RoiInputs } from './roiModel'

/** DC fast-charging ship defaults — "DCFC ROI" tab (2 ports × 150 kW). */
export const DCFC_PRESET: RoiInputs = {
  ports: 2,
  ratedPowerKw: 150,
  operatingDays: 365,
  kwhPerPortPerDay: 200,
  avgKwhPerSession: 22, // DOE: paid DCFC session avg ~22 kWh, ~42 min
  hardwarePerPort: 55000,
  makeReadyPerPort: 45000,
  electricalUpgradesSite: 40000,
  permittingSoftCostsSite: 15000,
  contingencySite: 10000,
  incentives: 0, // 30C expired 6/30/26 — enter state/utility/LCFS only
  utilityRatePerKwh: 0.18,
  demandChargePerKwMonth: 15,
  billedPeakKw: 150, // ~ one port at full power; less with load management
  networkPerPortYr: 1200,
  maintenancePerPortYr: 2000,
  insuranceFixedSiteYr: 1500,
  landLeaseSiteYr: 0,
  priceToDriverPerKwh: 0.55, // 2026 DCFC retail typically $0.40–$0.79/kWh
  idleFeeRevenueYr: 500,
  otherRevenueYr: 0,
  discountRate: 0.08,
  revenueGrowth: 0.03,
  costEscalation: 0.025,
}

/** Level 2 ship defaults — "L2 ROI" tab (4 ports × 9.6 kW). */
export const L2_PRESET: RoiInputs = {
  ports: 4,
  ratedPowerKw: 9.6,
  operatingDays: 365,
  kwhPerPortPerDay: 10, // benchmark ~6–15 kWh/port/day (NREL by venue)
  avgKwhPerSession: 8, // typical L2 dwell session; varies by venue
  hardwarePerPort: 1500,
  makeReadyPerPort: 5500,
  electricalUpgradesSite: 3000,
  permittingSoftCostsSite: 2000,
  contingencySite: 1000,
  incentives: 0, // 30C expired 6/30/26 — enter state/utility only
  utilityRatePerKwh: 0.18,
  demandChargePerKwMonth: 10, // usually minor for L2
  billedPeakKw: 20, // diversified peak across ports
  networkPerPortYr: 200,
  maintenancePerPortYr: 300,
  insuranceFixedSiteYr: 500,
  landLeaseSiteYr: 0,
  priceToDriverPerKwh: 0.4, // 2026 L2 retail typically $0.30–$0.60/kWh; workplace lower
  idleFeeRevenueYr: 300,
  otherRevenueYr: 0,
  discountRate: 0.08,
  revenueGrowth: 0.03,
  costEscalation: 0.025,
}

/** Charger-type key for selecting a preset. */
export type ChargerType = 'dcfc' | 'l2'

/** Ship-default preset by charger type — the "pick a tab" choice from the Read Me. */
export const ROI_PRESETS: Record<ChargerType, RoiInputs> = {
  dcfc: DCFC_PRESET,
  l2: L2_PRESET,
}

/** One row of the Read Me "BENCHMARK RANGES (2025–2026 US market)" table. */
export interface BenchmarkRange {
  /** Metric name, e.g. "Price to driver ($/kWh)". */
  metric: string
  /** Level 2 range as a display string, e.g. "$0.30 - $0.60". */
  l2: string
  /** DC fast range as a display string, e.g. "$0.40 - $0.79". */
  dcfc: string
  /** Attribution for the range. */
  source: string
}

/**
 * Read Me benchmark ranges, 2025–2026 US market. Captured now as structured data so the future UI
 * can surface them as helper text / range hints next to the matching input. Directional, not a quote.
 */
export const BENCHMARK_RANGES: BenchmarkRange[] = [
  {
    metric: 'Installed cost per port',
    l2: '$3,500 - $15,000',
    dcfc: '$75,000 - $250,000+',
    source: 'Recharged; NREL; TrendX 2026',
  },
  {
    metric: 'Hardware only per port',
    l2: '$500 - $2,500',
    dcfc: '$38,000 - $90,000',
    source: 'Greenlancer; TrendX 2026',
  },
  {
    metric: 'Energy dispensed / port / day',
    l2: '~6 - 15 kWh',
    dcfc: '~100 - 600 kWh',
    source: 'NREL venue study; comparepower',
  },
  {
    metric: 'Time utilization (healthy)',
    l2: '15% - 30%',
    dcfc: '15% - 25% (most sites)',
    source: 'Wins Parking; comparepower 2026',
  },
  {
    metric: 'Price to driver ($/kWh)',
    l2: '$0.30 - $0.60',
    dcfc: '$0.40 - $0.79',
    source: 'Wins Parking; TrendX 2026',
  },
  {
    metric: 'Demand charge share of bill',
    l2: 'small',
    dcfc: 'up to ~74% of C&I bill',
    source: 'NASEO demand charge study',
  },
  {
    metric: 'Typical simple payback',
    l2: '3 - 7 yrs',
    dcfc: '3 - 7 yrs (strong sites)',
    source: 'EV Connect; TrendX 2026',
  },
  {
    metric: 'Annual revenue per port/unit',
    l2: '$2,000 - $10,000',
    dcfc: '$20,000 - $50,000',
    source: 'EV Connect 2026',
  },
]

/**
 * Read Me "INCENTIVES — IMPORTANT" notes. Surfaced now as data so the UI can warn that the federal
 * 30C credit expired 2026-06-30 (One Big Beautiful Bill Act) and steer users toward what still
 * applies. Text is verbatim from the workbook.
 */
export const INCENTIVE_NOTES = {
  federal30cExpired:
    'The Alternative Fuel Vehicle Refueling Property Credit (30C) EXPIRED June 30, 2026 under the One Big Beautiful Bill Act. Equipment placed in service after that date gets $0 federal credit. Do not model it for new installs.',
  whatToEnterInstead:
    'State grants, utility make-ready rebates, LCFS credit revenue (where eligible), and demand-response payments. These still exist and can offset 30-90% of install cost in eligible programs. Enter only amounts you have confirmed.',
} as const
